import { Card, Tag, Editable, Text, Button } from "@ttc3k/trekker";
import { useTranslation } from "react-i18next";
import { getMultilingualString } from "shared/utils";
import { OperatorType } from "./Listings";
import { useListingCard } from "./useListingCard";
import { Flex, styled } from "styled-system/jsx";
import { OpenNewWindow } from "iconoir-react";
import { useLoggedInAs } from "shared/hooks";
import { ContactRole, EnumOwnerType, PersonDetailsQuery } from "gql/generated";

/**
 * Editable Title component for operator titles
 */
const OperatorTitleEditor = ({
  allListings,
  operatorListing,
  personId,
  operatorId,
  organizationId,
}: {
  allListings:
    | NonNullable<PersonDetailsQuery["personDetails"]>["operators"]
    | undefined
    | null;
  operatorListing: OperatorType;
  personId: string;
  operatorId: string;
  organizationId: string;
}) => {
  const { value, handleValueChange, handleValueCommit, t, isAdminland } =
    useListingCard(
      allListings,
      operatorListing,
      personId,
      operatorId,
      organizationId,
    );

  return isAdminland ? (
    <Text visual="smallRegular" color="text.mid">
      {value || t("NO_TITLE_SET")}
    </Text>
  ) : (
    <Editable
      label={t("EDITABLE_TITLE")}
      placeholder={t("EDITABLE_TITLE_PLACEHOLDER")}
      value={value}
      onValueChange={(d) => handleValueChange(d.value)}
      onValueCommit={handleValueCommit}
    />
  );
};

export const ListingCard = ({
  allListings,
  listing,
  personId,
}: {
  allListings:
    | NonNullable<PersonDetailsQuery["personDetails"]>["operators"]
    | undefined
    | null;
  listing: OperatorType;
  personId: string;
}) => {
  const { t } = useTranslation("core");
  const loggedInAs = useLoggedInAs();

  const navigateToOperator = (organizationId: string, operatorId: string) => {
    const url = `/app/organization/${organizationId}/directory/${operatorId}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };
  const getContactRoleLabel = (contactRole: ContactRole) =>
    contactRole === ContactRole.PrimaryContact
      ? t("STAFF.CONTACT_ROLES.PRIMARY_CONTACT")
      : t("STAFF.CONTACT_ROLES.FINANCIAL_CONTACT");

  return (
    <Card.Surface>
      <Card.Body p="0" gap="0">
        <Card.BodyContent
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap="400"
          py="200"
          px="300"
        >
          <Text visual="bodySemiBold" lineClamp={2}>
            {getMultilingualString(listing.name ?? {})}
          </Text>
          <Flex gap="100" flexWrap="wrap" justifyContent="flex-end">
            {listing.contactRoles
              ?.filter((contactRole): contactRole is ContactRole => !!contactRole)
              .map(contactRole => (
              <Tag
                key={contactRole}
                mode="light"
                visual={
                  contactRole === ContactRole.PrimaryContact
                    ? "accent.blue"
                    : "accent.purple"
                }
                flexShrink={0}
              >
                {getContactRoleLabel(contactRole)}
              </Tag>
            ))}
          </Flex>
        </Card.BodyContent>
        {/* Loop over each organization for this operator */}
        {listing.organizations
          ?.filter(org => !!org)
          .map(org => (
            <Card.BodyContent
              key={org?._id}
              py="200"
              px="300"
              borderTop="1px solid"
              borderColor="border.light"
              bg="ink.100"
              display="flex"
              justifyContent="space-between"
              gap="200"
            >
              {org?._id && (
                <OperatorTitleEditor
                  allListings={allListings}
                  operatorListing={listing}
                  personId={personId}
                  operatorId={listing._entityId}
                  organizationId={org._id}
                />
              )}
              <Flex flexShrink={0} gap="200" alignItems="center">
                <Button
                  color="text.mid"
                  visual="unstyled"
                  Icon={OpenNewWindow}
                  isIconRightAligned
                  size="sm"
                  onClick={() =>
                    navigateToOperator(org._id ?? "", listing._entityId)
                  }
                >
                  {loggedInAs?.type === EnumOwnerType.TnbInternal && (
                    <styled.span
                      maxW="165px"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {getMultilingualString(org.name ?? {})}
                    </styled.span>
                  )}
                </Button>
              </Flex>
            </Card.BodyContent>
          ))}
      </Card.Body>
    </Card.Surface>
  );
};
